import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";

export default function Terms() {
  useSeo({
    title: "Terms of Service · ARISSAI® · AI-Powered Project Intelligence",
    description: "Terms of service for using ARISSAI® — the AI-powered project intelligence SaaS for PMOs and project managers. Acceptable use, billing, data handling, and trademark policy.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen bg-zinc-100" data-testid="terms-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={32} tagline="AI-Driven Project Intelligence" />
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="terms-back-link">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Legal · Terms of Service</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3">Terms of Service</h1>
        <p className="mt-4 text-xs font-mono text-zinc-500">Last updated: 2026-02-17</p>

        <div className="mt-10 space-y-8 text-sm text-zinc-700 leading-relaxed">

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">1. Acceptance of Terms</h2>
            <p className="mt-2">By creating an ARISSAI account or using the ARISSAI platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">2. The Service</h2>
            <p className="mt-2">ARISSAI provides AI-powered project intelligence software through a six-lens analytical framework (Assumption, Risk, Issue, Solution, Insights, Integration). The Service is delivered as a subscription-based cloud product.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">3. Accounts</h2>
            <p className="mt-2">You must provide accurate registration information and maintain the security of your password. You are responsible for all activity under your account. You must be 18 or older to use the Service.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">4. Subscription &amp; Billing</h2>
            <p className="mt-2">Paid plans (Starter, Pro, Enterprise) are billed in advance on a monthly or annual cycle in Canadian dollars (CAD) via Stripe. Subscriptions auto-renew until cancelled from your Account page. Pricing may change with 30 days' notice for renewals.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">5. Refunds</h2>
            <p className="mt-2">Refund requests are handled on a case-by-case basis. Email <a href="mailto:hello@arissai.com?subject=ARISSAI%20refund%20request" className="text-[#0020F5] underline">hello@arissai.com</a> with your account email and the charge date. You can cancel any subscription at any time from your Account page to stop future renewals.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">6. Your Content</h2>
            <p className="mt-2">You retain all rights to project data you upload or create in ARISSAI. By using the Service, you grant ARISSAI a limited licence to process your content solely to provide the Service to you. We do not train AI models on your content. See our <Link to="/privacy" className="text-[#0020F5] underline">Privacy Notice</Link>.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">7. Acceptable Use</h2>
            <p className="mt-2">You agree not to: (a) upload illegal, infringing, or harmful content; (b) attempt to reverse-engineer or scrape the Service; (c) use the Service to compete with ARISSAI or to train a competing AI model; (d) interfere with the operation of the Service or attempt to access other users' data.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">8. AI Outputs Disclaimer</h2>
            <p className="mt-2">ARISSAI uses AI to generate analytical outputs (risk forecasts, mitigation plans, project insights). These outputs are decision-support tools, not professional advice. You are solely responsible for verifying AI outputs before acting on them. ARISSAI makes no warranty that AI outputs will be accurate, complete, or fit for any particular purpose.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">9. Intellectual Property</h2>
            <p className="mt-2">The ARISSAI platform, brand, logo, and six-lens methodology are the property of ARISSAI and protected by copyright and trademark laws. See <Link to="/legal" className="text-[#0020F5] underline">Trademarks &amp; IP</Link>.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">10. Termination</h2>
            <p className="mt-2">You may cancel anytime from your Account page. We may suspend or terminate accounts that violate these Terms. On termination, your access ends; you may export your data within 30 days, after which it is permanently deleted.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">11. Limitation of Liability</h2>
            <p className="mt-2">To the maximum extent permitted by law, ARISSAI's total liability for any claim arising from your use of the Service is limited to the amount you paid us in the 12 months preceding the claim. ARISSAI is not liable for indirect, incidental, or consequential damages.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">12. Changes to Terms</h2>
            <p className="mt-2">We may update these Terms from time to time. Material changes will be communicated by email or in-app notice at least 14 days before they take effect.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">13. Governing Law</h2>
            <p className="mt-2">These Terms are governed by the laws of the Province of Ontario, Canada, without regard to conflict-of-laws principles. Disputes shall be brought in the courts of Ontario.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-zinc-950">14. Contact</h2>
            <p className="mt-2">Questions about these Terms? Email <a href="mailto:hello@arissai.com" className="text-[#0020F5] underline">hello@arissai.com</a>.</p>
          </section>

        </div>

        <div className="mt-12 text-xs text-zinc-500 font-mono">ARISSAI® · © 2026 ARISSAI · All rights reserved.</div>
      </main>
    </div>
  );
}
